import React, { useEffect, useRef } from "react";
import { ActionIcon, Box, Text } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { MOBILE_Z } from "../tokens";
import type { ToastCopy } from "./bookingSheetModel";
import styles from "./MobileBooking.module.css";

/** Auto-dismiss delay (README screen 8). */
export const TOAST_MS = 3200;

export interface MobileToastState extends ToastCopy {
  /** Changes per toast so a repeat restarts the timer. */
  id: number;
}

export interface MobileToastProps {
  toast: MobileToastState | null;
  onDismiss: () => void;
}

/**
 * Completed-write toast (README screen 8, addendum 2.14): above the FAB, teal check bubble, title + note, dismiss X, auto-dismiss.
 */
export const MobileToast: React.FC<MobileToastProps> = ({
  toast,
  onDismiss,
}) => {
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const toastId = toast?.id;

  useEffect(() => {
    if (toastId == null) return undefined;
    const t = window.setTimeout(() => dismissRef.current(), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toastId]);

  if (!toast) return null;
  return (
    <Box
      className={styles.toast}
      style={{ zIndex: MOBILE_Z.toast }}
      role="status"
      data-testid="mobile-toast"
    >
      <Box component="span" className={styles.toastMark}>
        <IconCheck size={15} stroke={3} />
      </Box>
      <Box component="span" className={styles.toastText}>
        <Text component="span" className={styles.toastTitle}>
          {toast.title}
        </Text>
        {toast.note && (
          <Text component="span" className={styles.toastNote}>
            {toast.note}
          </Text>
        )}
      </Box>
      <ActionIcon
        variant="transparent"
        className={styles.toastClose}
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <IconX size={16} stroke={1.8} />
      </ActionIcon>
    </Box>
  );
};
